import { useEffect, useState } from "react";
import { getToken, setToken, clearToken, login, verifyAuth } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { toast } from "sonner";

export default function PinGate({ children }) {
  const [status, setStatus] = useState("checking"); // checking | locked | unlocked
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (!token) { setStatus("locked"); return; }
    verifyAuth()
      .then(() => setStatus("unlocked"))
      .catch(() => { clearToken(); setStatus("locked"); });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { token } = await login(pin.trim());
      setToken(token);
      setStatus("unlocked");
    } catch (err) {
      if (err?.response?.status === 429) toast.error("Too many attempts. Try again in a bit.");
      else toast.error("Incorrect PIN");
      setPin("");
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <span className="mono text-sm text-muted-foreground dot-pulse">Checking access…</span>
      </div>
    );
  }

  if (status === "locked") {
    return (
      <div className="min-h-screen grid place-items-center bg-background grid-bg">
        <form onSubmit={submit} className="w-full max-w-xs space-y-4 border border-border rounded-md bg-card p-6" data-testid="pin-gate-form">
          <div className="flex items-center gap-2 justify-center">
            <Lock className="h-4 w-4 text-primary" />
            <span className="mono text-sm tracking-[0.2em] uppercase text-primary terminal-glow">Consultant.OS</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">Enter PIN to continue</p>
          <Input
            data-testid="pin-input"
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            className="bg-muted border-border mono text-center tracking-[0.3em] text-lg"
            placeholder="••••"
          />
          <Button
            type="submit"
            data-testid="pin-submit-btn"
            disabled={submitting || !pin.trim()}
            className="w-full bg-primary text-primary-foreground hover:bg-secondary hover:text-white mono uppercase tracking-widest text-xs"
          >
            {submitting ? "Checking…" : "Unlock"}
          </Button>
        </form>
      </div>
    );
  }

  return children;
}
